import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import Button from "../components/ui/Button";

function isUuid(v) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v || "");
}

export default function OrderConfirmation() {
  const { id } = useParams(); // this can be uuid OR order_number depending on how you navigate
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  const title = useMemo(() => {
    if (!order) return "Order";
    return order.order_number ? `Order ${order.order_number}` : `Order ${order.id?.slice(0, 8)}`;
  }, [order]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setErr("");
      setOrder(null);

      try {
        if (!id) {
          throw new Error("Missing order id in URL.");
        }

        // Try by UUID id first if it looks like a uuid
        let q = supabase.from("orders").select("*");

        let res;
        if (isUuid(id)) {
          res = await q.eq("id", id).single();
        } else {
          // Otherwise try order_number
          res = await q.eq("order_number", id).single();
        }

        if (res.error) throw res.error;

        if (mounted) setOrder(res.data);
      } catch (e) {
        if (mounted) setErr(e?.message || "Could not load order.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <div className="text-lg font-semibold text-slate-900">Loading your order…</div>
            <div className="mt-2 text-sm text-slate-600">One moment.</div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
            <div className="text-xl font-semibold text-slate-900">Order not found</div>
            <div className="mt-2 text-sm text-slate-600">
              {err || "This order may not exist or is not readable with your current settings."}
            </div>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Link to="/shop">
                <Button>Back to shop</Button>
              </Link>
              <Link to="/cart">
                <Button variant="secondary">Back to cart</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const total = Number(order.total || 0).toFixed(2);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between gap-6">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">{title}</h1>
            <p className="mt-2 text-slate-600">
              Thanks — your order was received. Please follow the payment instructions below.
            </p>
          </div>
          <Link to="/shop" className="text-sm text-slate-700 hover:text-slate-900">
            ← Continue shopping
          </Link>
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* Receipt */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600">
                Order status: <span className="font-semibold text-slate-900">{order.status}</span>
              </div>
              <div className="text-sm text-slate-600">
                Placed:{" "}
                <span className="font-semibold text-slate-900">
                  {order.created_at ? new Date(order.created_at).toLocaleString() : "—"}
                </span>
              </div>
            </div>

            <div className="mt-5 border-t border-slate-200 pt-5">
              <div className="text-sm font-semibold text-slate-900">Items</div>
              <div className="mt-4 space-y-3">
                {(order.items || []).map((i, idx) => (
                  <div key={`${i.id}-${idx}`} className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{i.name}</div>
                      <div className="text-xs text-slate-600">
                        {i.qty} × ${Number(i.price).toFixed(2)}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-slate-900">
                      ${Number(i.lineTotal ?? (i.price * i.qty)).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 border-t border-slate-200 pt-4 space-y-2 text-sm">
                <Row label="Subtotal" value={`$${Number(order.subtotal || 0).toFixed(2)}`} />
                <Row label="Shipping" value={`$${Number(order.shipping || 0).toFixed(2)}`} />
                <Row
                  label={<span className="font-semibold text-slate-900">Total</span>}
                  value={<span className="font-semibold text-slate-900">${total}</span>}
                />
              </div>
            </div>
          </div>

          {/* e-Transfer instructions */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:sticky lg:top-24">
              <div className="text-sm font-semibold text-slate-900">Payment</div>

              <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                <div className="font-semibold">Interac e-Transfer</div>
                <div className="mt-2 text-blue-800 space-y-2">
                  <div>
                    Send <span className="font-semibold">${total} CAD</span> to:
                  </div>
                  <div className="font-semibold underline">
                    payments@hdlabs.ca
                  </div>
                  <div>
                    Memo / note:{" "}
                    <span className="font-semibold">
                      {order.order_number || order.id}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-xs text-slate-600 leading-relaxed">
                Once payment is received, your order status will be updated. If you have any issues,
                reply to your order email with your order number.
              </div>

              <div className="mt-5">
                <Link to="/shop">
                  <Button className="w-full">Back to shop</Button>
                </Link>
                <Link to="/cart" className="block mt-3">
                  <Button variant="secondary" className="w-full">
                    View cart
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-slate-600">{label}</div>
      <div className="text-slate-900">{value}</div>
    </div>
  );
}
